import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { mimeTypeToPcpType } from '@wix/da-shared-react/pkg/utils/mime';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { FileUploadOptions } from '@wix/da-shared-react/pkg/utils/hooks/useFileUpload';
import { useUnlockWithCoreUpsellModal } from './useUnlockWithCoreUpsellModal';

export interface ReturnType
  extends Pick<FileUploadOptions, 'validator' | 'onDropRejected'> {
  modal: React.ReactNode;
}

export const LOCKED_VIDEO_UPLOAD_FILEERROR = {
  message: 'locked video upload',
  code: '',
};

/** Returns:
 *  - a dialog telling the user to upgrade to core
 *  - and a function that will show that dialog if any of its given files
 *  being uploaded are the video type (to be called in useFileUpload's onDrop)
 */
export function useUnlockVideoUploadsCheck(): ReturnType {
  const { t } = useTranslation();

  const { hasCoreMembership } = useContext(SessionContext);
  const areVideoUploadsLocked = !hasCoreMembership;

  const { modal, showModal } = useUnlockWithCoreUpsellModal({
    // note: in da-shared's locale
    title: t('deviationSubmit.unlockVideoUploads.title'),
    body: t('deviationSubmit.unlockVideoUploads.body'),
    cta: t('deviationSubmit.unlockVideoUploads.cta'),
    referralType: ReferralType.SUBMIT_VIDEO,
  });

  return {
    /** dialog displayed if any file is a video and the viewer doesn't have
     *  core. place in your render tree
     *  !IMPORTANT: place anywhere -EXCEPT- inside the dropzone element itself
     *    or this will cause a re-focusing loop between the dropzone and the modal */
    modal,
    // note: the following two can be combined/chained with other validators
    // and rejection handlers
    validator: useCallback(
      file => {
        const isVideoMime = mimeTypeToPcpType(file.type) === 'video';
        return areVideoUploadsLocked && isVideoMime
          ? LOCKED_VIDEO_UPLOAD_FILEERROR
          : null;
      },
      [areVideoUploadsLocked]
    ),
    onDropRejected: useCallback(
      rejected => {
        const hasRejectedVideo = rejected.some(rejection => {
          return rejection.errors.includes(LOCKED_VIDEO_UPLOAD_FILEERROR);
        });
        hasRejectedVideo && showModal();
      },
      [showModal]
    ),
  };
}
